import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

export interface SegmentedTabOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
}

export interface SegmentedTabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedTabOption<T>[];
  size?: 'sm' | 'md';
  /** Rozciągnij tabsy na pełną szerokość kontenera. */
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * Pill-style compact tabs dla krótkich, ekspozycyjnych wyborów
 * (Desktop/Tablet/Mobile, quick presets, filtry szybkie). 8+ miejsc w kodzie.
 *
 * NIE używaj dla głównej nawigacji między widokami strony - tam używaj
 * <Tabs>. SegmentedTabs są dla switcherów w środku ekranów.
 */
export function SegmentedTabs<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  fullWidth = false,
  className,
  ...rest
}: SegmentedTabsProps<T>) {
  const btnPad = size === 'sm' ? 'px-2.5 py-1 text-xs gap-1.5' : 'px-3 py-1.5 text-sm gap-2';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={[
        'inline-flex gap-1 p-1 rounded-xl bg-surface-raised/60 border border-border-subtle',
        fullWidth ? 'w-full' : 'w-fit',
        className,
      ].filter(Boolean).join(' ')}
    >
      {options.map(opt => {
        const isActive = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={opt.disabled}
            onClick={() => !opt.disabled && onChange(opt.value)}
            className={[
              'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-fast',
              btnPad,
              fullWidth ? 'flex-1' : '',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40',
              isActive
                ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-transparent',
            ].filter(Boolean).join(' ')}
          >
            {Icon && <Icon className={`${iconSize} shrink-0`} aria-hidden="true" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
